import { Injectable } from "@angular/core";
import { StaffRequirementsService } from "lib-klab-sdks";
import { map, Observable } from "rxjs";
import { RequirementsPort } from "../../../app/ports/requirement/requirements.port";
import {
  Requirement, RequirementListRequest, RequirementPage, RequirementRecord,
  RequirementRequest, RequirementStatuses, RequirementSummary, RequirementSummaryRequest,
} from "../../../domain/models/requirement/requirement.model";
import { mapRequirement, mapRequirementRecord } from "../../mappers/requirement/requirement.mapper";

@Injectable({ providedIn: "root" })
export class RequirementsPortAdapter implements RequirementsPort {
  constructor(private readonly api: StaffRequirementsService) {}

  list(request: RequirementListRequest): Observable<RequirementPage> {
    return this.api.list(request).pipe(
      map(({ data }) => ({ ...data, items: data.items.map(mapRequirement) })),
    );
  }

  listMine(request: Omit<RequirementListRequest, "requesterUserId"> = {}): Observable<RequirementPage> {
    return this.api.listMine(request).pipe(
      map(({ data }) => ({ ...data, items: data.items.map(mapRequirement) })),
    );
  }

  get(id: number): Observable<Requirement> {
    return this.api.get(id).pipe(map(({ data }) => mapRequirement(data)));
  }

  changeDocumentStatus(id: number, status: string, comment: string): Observable<RequirementRecord> {
    return this.api.changeDocumentStatus(id, status, comment).pipe(
      map(({ data }) => mapRequirementRecord(data)),
    );
  }

  create(request: RequirementRequest): Observable<RequirementRecord> {
    return this.api.create(request).pipe(map(({ data }) => mapRequirementRecord(data)));
  }

  update(id: number, request: RequirementRequest): Observable<RequirementRecord> {
    return this.api.update(id, request).pipe(map(({ data }) => mapRequirementRecord(data)));
  }

  delete(id: number): Observable<void> {
    return this.api.delete(id).pipe(map(() => undefined));
  }

  listStatuses(): Observable<RequirementStatuses> {
    return this.api.listStatuses().pipe(map(({ data }) => data));
  }

  summary(request: RequirementSummaryRequest = {}): Observable<RequirementSummary> {
    return this.api.summary(request).pipe(map(({ data }) => data));
  }

  mineSummary(month?: string): Observable<RequirementSummary> {
    return this.api.mineSummary(month).pipe(map(({ data }) => data));
  }
}
